import React, { useState, useEffect, useRef, useCallback, PropsWithChildren } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { cancelTimeout, requestTimeout, TimeoutID } from 'helpers/timer'
import { APIError, ApiErrorCodes, ApiErrorLink } from 'helpers/api'
import { shouldOfferSupport } from 'helpers/apiError'
import { safeLocalStorage } from 'helpers/localStorage'
import Button from 'components/button'
import { HUB, HUB_BASE } from 'routes'
import { ErrorContent, ISnackbarObject } from 'types'
import { ApplicationContext, ApplicationStore } from 'hooks/application'
import { AUTH_KEY, SIDEBAR_MIN_WIDTH } from 'app-constants'

function collectValidationMessages(errors: Array<ErrorContent>): string[] {
  return errors.flatMap((err) => {
    const attr =
      typeof err.attribute === 'string'
        ? err.attribute
        : err.attribute && typeof err.attribute === 'object'
          ? JSON.stringify(err.attribute)
          : ''

    if (err.messages && typeof err.messages === 'object' && !Array.isArray(err.messages)) {
      return Object.entries(err.messages).flatMap(([subField, msgs]) => {
        if (msgs && typeof msgs === 'object' && !Array.isArray(msgs)) {
          return Object.entries(msgs).flatMap(([nestedField, nestedMsgs]) =>
            (Array.isArray(nestedMsgs) ? nestedMsgs : [String(nestedMsgs)]).map(
              (m: string) => `${attr ? attr + '.' : ''}${subField}.${nestedField}: ${m}`
            )
          )
        }
        return (Array.isArray(msgs) ? msgs : [String(msgs)]).map(
          (m: string) => `${attr ? attr + '.' : ''}${subField}: ${m}`
        )
      })
    }
    if (Array.isArray(err.messages)) {
      return err.messages.map((m: string) => `${attr ? attr + ': ' : ''}${m}`)
    }
    return []
  })
}

export const ApplicationProvider: React.FC<PropsWithChildren> = ({ children }) => {
  'use no memo'
  const location = useLocation()

  const [snackbarMessage, updateSnackbarMessage] = useState<ISnackbarObject>({
    message: '',
    status: 'noShow',
    duration: 8000
  })
  const [sidebarWidth, updateSidebarWidth] = useState<number>(0)
  const [templatesModal, setTemplatesModal] = useState<boolean>(false)
  const [supportChatContext, setSupportChatContext] = useState<ApplicationStore['supportChatContext']>(null)
  const [agentChatLaunchContext, setAgentChatLaunchContext] = useState<ApplicationStore['agentChatLaunchContext']>(null)
  const timeoutRef = useRef<TimeoutID | null>(null)
  const lastSupportChatErrorRef = useRef<{ errorMessage: string; timestamp: number } | null>(null)

  const setSidebarWidth = useCallback((newSidebarWidth: number) => {
    safeLocalStorage.setItem('mortaSidebarWidth', newSidebarWidth.toString())
    updateSidebarWidth(newSidebarWidth)
  }, [])

  const setSnackbarMessage = useCallback((newSnackbarObject: ISnackbarObject) => {
    updateSnackbarMessage((prev) => {
      if (
        prev.message === newSnackbarObject.message &&
        prev.status === newSnackbarObject.status &&
        prev.duration === newSnackbarObject.duration &&
        prev.path === newSnackbarObject.path &&
        prev.method === newSnackbarObject.method &&
        prev.offerSupport === newSnackbarObject.offerSupport
      ) {
        return prev
      }
      return newSnackbarObject
    })

    if (timeoutRef.current) {
      cancelTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    if (newSnackbarObject.status !== 'error') {
      const delay = newSnackbarObject.duration === undefined ? 5000 : newSnackbarObject.duration
      timeoutRef.current = requestTimeout(() => {
        updateSnackbarMessage({ status: 'noShow' } as any)
      }, delay)
    }
  }, [])

  const openSupportChat = useCallback(
    (context: { errorMessage: string; diagnostics: string; path?: string; method?: string } | null) => {
      if (context !== null) {
        const now = Date.now()
        const last = lastSupportChatErrorRef.current
        if (last && last.errorMessage === context.errorMessage && now - last.timestamp < 10000) {
          return
        }
        lastSupportChatErrorRef.current = { errorMessage: context.errorMessage, timestamp: now }
      }
      setSupportChatContext(context)
    },
    []
  )

  const clearAgentChatLaunchContext = useCallback(() => setAgentChatLaunchContext(null), [])

  const clearSnackbar = useCallback(() => {
    if (timeoutRef.current) {
      cancelTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    updateSnackbarMessage({ status: 'noShow' } as any)
  }, [])

  const displayErrorMessage = useCallback(
    (e: any) => {
      if (e instanceof APIError) {
        let tableId: string | undefined
        let tableViewId: string | undefined
        let links: ApiErrorLink[] | undefined

        if (e.links && Array.isArray(e.links) && e.links.length > 0) {
          links = e.links
        }

        if (e.code === ApiErrorCodes.UNAUTHORIZED) return

        // Don't show 403 permission errors when user is not authenticated
        // This prevents "You do not have appropriate permissions" from showing when redirecting to sign-in
        if (e.rawCode === 403 && !safeLocalStorage.getItem(AUTH_KEY)) return

        if (e.code === ApiErrorCodes.SUBSCRIPTION_LEVEL) {
          setSnackbarMessage({
            status: 'subscription',
            message: e.message,
            duration: 20000
          })
        } else {
          if (e.errors && e.errors.length > 0) {
            const primaryError = e.errors[0]
            if (
              'attribute' in primaryError &&
              typeof primaryError['attribute'] === 'object' &&
              primaryError['attribute'] &&
              'tableId' in primaryError['attribute'] &&
              'tableViewId' in primaryError['attribute']
            ) {
              tableId = (primaryError['attribute'] as any)['tableId']
              tableViewId = (primaryError['attribute'] as any)['tableViewId']
            }
          }

          if (e.errors && e.errors.length > 0) {
            const primaryError = e.errors[0]
            if (
              primaryError['attribute'] === '_schema' &&
              Array.isArray(primaryError['messages']) &&
              primaryError['messages'].length > 0
            ) {
              setSnackbarMessage({
                status: 'error',
                message: primaryError.messages[0],
                path: e.path,
                method: e.method,
                offerSupport: shouldOfferSupport(e)
              })
              return
            }
          }

          if (e.code === ApiErrorCodes.REQUEST_PARAMS_ERROR && Array.isArray(e.errors)) {
            const messages = collectValidationMessages(e.errors)
            setSnackbarMessage({
              status: 'error',
              message: messages.length > 0 ? messages.join(' • ') : e.message,
              path: e.path,
              method: e.method,
              offerSupport: shouldOfferSupport(e)
            })
            return
          }

          const projectId = matchPath(`${HUB}/*`, location?.pathname ?? '')?.params.projectId

          setSnackbarMessage({
            status: 'error',
            message: e.message,
            action:
              projectId && tableId && tableViewId ? (
                <a
                  href={`${HUB_BASE}${projectId}/table/${tableId}/view/${tableViewId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="ml-2.5" internalType="outline">
                    Open Table
                  </Button>
                </a>
              ) : projectId && links && links.length > 0 ? (
                <div className="flex items-center text-left">
                  <ol>
                    {links.map((u) => (
                      <li className="mt-[5px]" key={u.link}>
                        <a href={u.link} target="_blank" rel="noopener noreferrer" className="underline text-on-solid">
                          {u.name}
                        </a>
                      </li>
                    ))}
                  </ol>
                </div>
              ) : (
                // Keep div here to avoid layout shift (snackbar height changing)
                <div />
              ),
            path: e.path,
            method: e.method,
            offerSupport: shouldOfferSupport(e)
          })
        }
      } else if (typeof e === 'string') {
        setSnackbarMessage({ status: 'error', message: e } as any)
      } else {
        setSnackbarMessage({ status: 'error' } as any)
      }
    },
    [setSnackbarMessage, location]
  )

  useEffect(() => {
    const saved = safeLocalStorage.getItem('mortaSidebarWidth')
    if (saved) {
      const parsed = Number.parseInt(saved)
      // A hidden sidebar (0) stays hidden; anything narrower than the minimum (from before there
      // was one) opens at the minimum, never as a slim strip.
      if (Number.isNaN(parsed)) return
      updateSidebarWidth(parsed <= 0 ? 0 : Math.max(parsed, SIDEBAR_MIN_WIDTH))
    }
  }, [])

  const value: ApplicationStore = {
    snackbarMessage,
    setSnackbarMessage,
    sidebarWidth,
    setSidebarWidth,
    minSidebarWidth: SIDEBAR_MIN_WIDTH,
    maxSidebarWidth: 600,
    templatesModal,
    setTemplatesModal,
    displayErrorMessage,
    clearSnackbar,
    supportChatContext,
    openSupportChat,
    agentChatLaunchContext,
    launchAgentChat: setAgentChatLaunchContext,
    clearAgentChatLaunchContext
  }

  return <ApplicationContext.Provider value={value}>{children}</ApplicationContext.Provider>
}
